import { useCallback, useContext, useMemo } from "react";

import { InfoCircleOutlined } from "@ant-design/icons";
import { ConditionalType } from "@regal-voice/shared-types";
import { Form, Input, Select, Popover } from "antd";
import cloneDeep from "lodash/cloneDeep";
import set from "lodash/set";

import {
    RVFormFloatingLabel,
    RVFormItem,
    ConfigurableVariableOption,
    ConfigurableVariablesPickerPlugin,
    CustomVariablesHighlightPlugin,
    PlainTextEditor,
    HANDLEBAR_TRIGGERS,
    HANDLEBARS_WRAP,
} from "@regal-voice/ui";
import { AutocompleteStringValue } from "Components/shared/AutoCompleteStringValue/AutocompleteStringValue";
import { RVSearchableMultiselectField } from "Components/shared/RVSearchableMultiselectField/RVSearchableMultiselectField";
import { JourneyFormContext } from "Pages/orchestration/Pages/journeys/JourneyEditPage/Components/JourneyBuilder/shared/Contexts/JourneyFormContext";
import { useQueryEventAttributes } from "Pages/orchestration/Pages/journeys/JourneyEditPage/Components/JourneyBuilder/shared/hooks/useQueryEventAttributes";
import { createOptionsWithPrefixWithOriginalFieldName } from "ServerData/EventSchema/Utils/eventSchemaUtils";
import { NodeTriggerType } from "Types/Journey";

import { NumericInputValue } from "./NumericInputValue";

import styles from "../ConditionalFormItems.module.scss";

const HANDLEBARS_REGEX_LIST = [HANDLEBARS_WRAP];

export function ConditionalNodePropertyValue({
    rootField,
    propertyType,
    propertyOperator,
    propertyValueRequired,
    propertyValueDisabled,
    propertyFieldId,
    isContactCondtional,
    triggeringEvent,
}: {
    rootField?: any;
    propertyType?: any;
    propertyOperator: string;
    propertyValueRequired: any;
    propertyValueDisabled: boolean;
    propertyFieldId: string;
    isContactCondtional: boolean;
    triggeringEvent: { eventType?: NodeTriggerType | ConditionalType; eventName?: string };
}): JSX.Element {
    // why do we need to assert this?
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const { formSubmited, form } = useContext(JourneyFormContext)!;

    /* Getting the event fields */
    const { loading, eventAttributes } = useQueryEventAttributes(triggeringEvent);
    const eventOptions: { value: string; label: string; dataType: string }[] = useMemo(() => {
        if (!loading && eventAttributes.length > 0) {
            return eventAttributes
                .filter((event) => event.fieldType === propertyType)
                .map((event) => createOptionsWithPrefixWithOriginalFieldName(event, "event"));
        }
        return [];
    }, [loading, eventAttributes, propertyType]);

    const allOptions = useMemo<Array<ConfigurableVariableOption>>(
        () => eventOptions.map((option) => new ConfigurableVariableOption(option.label, option.value, option.dataType)),
        [eventOptions]
    );

    const propertyValuePlainInputTypes = [undefined, null, "list"];

    const validatePropertyTypeIsNotObject = useCallback(
        async (_: any, propertyType: any) => {
            if (!formSubmited) {
                return Promise.resolve();
            }
            if (propertyType == "object") {
                return Promise.reject(new Error("Cannot perform equality check on object"));
            }
            return Promise.resolve();
        },
        [formSubmited]
    );

    function getFilterFieldValue(formValues: any, filterIndex: number, fieldName: string) {
        const { filters } = formValues;
        const currentFilter = filters[filterIndex];
        return currentFilter[fieldName];
    }

    function handleChange(propertyValue: string) {
        if (form) {
            const propertyValueType =
                getFilterFieldValue(form.getFieldsValue(), rootField.name, "propertyValueType") || "";

            let newPropValueType = null;
            if (propertyValueType != "variable" && propertyValue.startsWith("{{")) {
                newPropValueType = "variable";
            } else if (propertyValueType != "literal" && !propertyValue.startsWith("{{")) {
                newPropValueType = "literal";
            }

            // update propertyValueType if needed
            if (newPropValueType != null) {
                const formValuesCloned = cloneDeep(form.getFieldsValue());
                set(formValuesCloned, ["filters", rootField.name, "propertyValueType"], newPropValueType);
                form.setFieldsValue(formValuesCloned);
            }
        }
    }

    const getIconWithTooltip = () => {
        return (
            <Popover
                title={"Dynamic References"}
                placement="topRight"
                content={
                    <div className={styles.smallPopover}>
                        To dynamically reference property values from the triggering event, use&nbsp;
                        <span className={styles.exampleHandlebars}>&#123;&#123;handle bars&#125;&#125;</span>&nbsp;and
                        prepend property name with <b>event</b>. The available values for comparison are limited to the
                        property field type above.&nbsp;
                        <a
                            className="external-link"
                            target="_blank"
                            href="https://support.regal.ai/hc/en-us/articles/14157351216027-How-to-Use-Dynamic-Reference-in-Conditional-Node"
                            rel="noreferrer"
                        >
                            More Details
                        </a>
                    </div>
                }
            >
                <InfoCircleOutlined className={styles.relativeDatePopOverIcon} />
            </Popover>
        );
    };

    return (
        <>
            {["string"].includes(propertyType) && (
                <RVFormFloatingLabel
                    {...(rootField
                        ? { name: ["filters", rootField.name, "propertyValue"] }
                        : { name: "propertyValue" })}
                    key={`floating-propertyValueAutocomplete-${rootField ? rootField.name : 1}`}
                >
                    <RVFormItem
                        key={`propertyValueAutocomplete-${rootField ? rootField.name : 1}`}
                        {...(rootField
                            ? {
                                  ...rootField,
                                  name: [rootField.name, "propertyValue"],
                                  dependencies: [[rootField.name, "propertyName"], "eventName"],
                              }
                            : { name: "propertyValue", dependencies: ["propertyName", "eventName"] })}
                        rules={[
                            {
                                validator: validatePropertyTypeIsNotObject,
                                message:
                                    'Cannot perform equality check on Object data type. To check selected Object exists, use "Is Set"',
                            },
                            {
                                validator: propertyValueRequired,
                            },
                        ]}
                        className={styles.formItem}
                        label={rootField ? (isContactCondtional ? "Attribute Value" : "Property Value") : undefined}
                    >
                        {propertyOperator === "isOneOf" ? (
                            <RVSearchableMultiselectField
                                autoClearSearchValue={false}
                                maxTagCount={4}
                                propertyFieldId={propertyFieldId}
                                dataTestid={`property-select-${rootField.name}-propertyValue-string`}
                            />
                        ) : isContactCondtional ? (
                            <AutocompleteStringValue
                                rootField={rootField}
                                propertyOperator={propertyOperator}
                                propertyValueDisabled={propertyValueDisabled}
                                propertyFieldId={propertyFieldId}
                                isContactCondtional={isContactCondtional}
                                eventOptions={eventOptions}
                                dataTestid={`property-select-${rootField.name}-propertyValue-string`}
                            />
                        ) : (
                            <EventPropertyValueEditor
                                allOptions={allOptions}
                                loading={loading}
                                disabled={propertyValueDisabled}
                                onChange={handleChange}
                                rootField={rootField}
                                suffix={getIconWithTooltip()}
                                dataTestid={`property-select-${rootField.name}-propertyValue-string`}
                            />
                        )}
                    </RVFormItem>
                </RVFormFloatingLabel>
            )}
            {propertyValuePlainInputTypes?.includes(propertyType) && (
                <RVFormFloatingLabel
                    {...(rootField
                        ? { name: ["filters", rootField.name, "propertyValue"] }
                        : { name: "propertyValue" })}
                    key={`floating-propertyValueNonAutocomplete-${rootField ? rootField.name : 1}`}
                >
                    <RVFormItem
                        key={`propertyValueNonAutocomplete-${rootField ? rootField.name : 1}`}
                        {...(rootField
                            ? {
                                  ...rootField,
                                  name: [rootField.name, "propertyValue"],
                                  dependencies: [[rootField.name, "propertyValue"], "eventName"],
                              }
                            : { name: "propertyValue", dependencies: ["propertyName", "eventName"] })}
                        rules={[
                            {
                                validator: async (_, propertyType) => {
                                    if (propertyType == "object") {
                                        return Promise.reject(new Error("Cannot perform equality check on object"));
                                    }
                                    return Promise.resolve();
                                },
                                message:
                                    'Cannot perform equality check on Object data type. To check selected Object exists, use "Is Set"',
                            },
                            {
                                validator: propertyValueRequired,
                            },
                        ]}
                        className={styles.formItem}
                        label={rootField ? (isContactCondtional ? "Attribute Value" : "Property Value") : undefined}
                    >
                        <Input
                            placeholder={
                                !rootField ? (isContactCondtional ? "Attribute Value" : "Property Value") : undefined
                            }
                            disabled={propertyValueDisabled}
                        />
                    </RVFormItem>
                </RVFormFloatingLabel>
            )}
            {propertyType === "number" && (
                <RVFormFloatingLabel
                    {...(rootField
                        ? { name: ["filters", rootField.name, "propertyValue"] }
                        : { name: "propertyValue" })}
                    key={`floating-propertyValueNumeric-${rootField ? rootField.name : 1}`}
                >
                    <RVFormItem
                        {...(rootField
                            ? {
                                  ...rootField,
                                  name: [rootField.name, "propertyValue"],
                                  dependencies: [[rootField.name, "propertyName"], "eventName", "attributeName"],
                              }
                            : { name: "propertyValue", dependencies: ["propertyName", "eventName", "attributeName"] })}
                        rules={[
                            {
                                validator: propertyValueRequired,
                            },
                        ]}
                        key={`propertyValueNumeric-${rootField ? rootField.name : 1}`}
                        className={styles.formItem}
                        label={rootField ? "Enter Integer or Decimal" : undefined}
                    >
                        <NumericInputValue
                            rootField={rootField}
                            propertyOperator={propertyOperator}
                            propertyValueDisabled={propertyValueDisabled}
                        />
                    </RVFormItem>
                </RVFormFloatingLabel>
            )}
            {propertyType === "boolean" && (
                <RVFormFloatingLabel
                    {...(rootField
                        ? { name: ["filters", rootField.name, "propertyValue"] }
                        : { name: "propertyValue" })}
                    key={`floating-boolean-${rootField ? rootField.name : 1}`}
                >
                    <RVFormItem
                        initialValue="true"
                        {...(rootField
                            ? {
                                  ...rootField,
                                  name: [rootField.name, "propertyValue"],
                                  dependencies: [[rootField.name, "propertyName"], "eventName"],
                              }
                            : { name: "propertyValue", dependencies: ["propertyName", "eventName"] })}
                        rules={[
                            {
                                validator: validatePropertyTypeIsNotObject,
                                message:
                                    'Cannot perform equality check on Object data type. To check selected Object exists, use "Is Set"',
                            },
                            {
                                validator: propertyValueRequired,
                            },
                        ]}
                        key={`proeprty-value-boolean-${rootField ? rootField.name : 1}`}
                        className={styles.formItem}
                        label={rootField ? (isContactCondtional ? "Attribute Value" : "Property Value") : undefined}
                    >
                        <Select>
                            <Select.Option value="true">True</Select.Option>
                            <Select.Option value="false">False</Select.Option>
                        </Select>
                    </RVFormItem>
                </RVFormFloatingLabel>
            )}
            <RVFormItem
                hidden={true}
                {...(rootField
                    ? {
                          ...rootField,
                          name: [rootField.name, "propertyValueType"],
                          dependencies: [[rootField.name, "propertyName"], "eventName"],
                      }
                    : { name: "propertyValueType", dependencies: ["propertyName", "eventName"] })}
            >
                <Input />
            </RVFormItem>
        </>
    );
}

function EventPropertyValueEditor({
    allOptions,
    loading,
    disabled,
    onChange,
    rootField,
    suffix,
    dataTestid,
}: {
    allOptions: Array<ConfigurableVariableOption>;
    loading: boolean;
    disabled: boolean;
    onChange: (value: string) => void;
    rootField?: any;
    suffix?: JSX.Element;
    dataTestid?: string;
}) {
    const form = Form.useFormInstance();
    const fieldPath = rootField ? ["filters", rootField.name, "propertyValue"] : ["propertyValue"];
    const initialValue = form.getFieldValue(fieldPath);

    const handleChange = useCallback(
        (newValue: string) => {
            const fieldObj = rootField
                ? { filters: { [rootField.name]: { propertyValue: newValue } } }
                : { propertyValue: newValue };
            form.setFieldsValue(fieldObj);
            onChange(newValue);
        },
        [form, onChange, rootField]
    );

    return (
        <div className={styles.dynamicComparisonEditor}>
            <PlainTextEditor
                className={styles.editorWrapper}
                inputClassName={styles.editorInput}
                data-testid={dataTestid}
                value={initialValue}
                onChange={handleChange}
                singleRow
                disabled={disabled}
            >
                <CustomVariablesHighlightPlugin prioritiziedRegexList={HANDLEBARS_REGEX_LIST} />
                <ConfigurableVariablesPickerPlugin
                    popAbove
                    fixedWidth
                    loading={loading}
                    options={allOptions}
                    prioritizedTriggers={HANDLEBAR_TRIGGERS}
                />
            </PlainTextEditor>
            {suffix}
        </div>
    );
}
